<template>
  <f7-page :page-content="true">
    <f7-navbar :back-link="$t('atras')">
      <f7-nav-title v-if="id && !readonly" sliding>{{$t('gastos.editar')}}</f7-nav-title>
      <f7-nav-title v-else-if="id" sliding>{{$t('gastos.gasto')}}</f7-nav-title>
      <f7-nav-title v-else sliding>{{$t('gastos.nuevo')}}</f7-nav-title>
      <f7-nav-right>
        <template v-if="!readonly">
          <f7-button v-if="id" @click="cancelarEdicion">{{$t('common.cancelar')}}</f7-button>
          <!-- Mientras la IA lee no se guarda: se persistiría la propuesta anterior (o nada) en vez de la nueva. -->
          <f7-button fill icon-f7="floppy_disk" :text="$t('common.guardar')" class="display-flex" :disabled="leyendo" @click="guardar"></f7-button>
        </template>
        <template v-else>
          <f7-button v-if="puedeEditar" color="blue" @click="readonly = false">{{$t('common.editar')}}</f7-button>
          <f7-button v-if="puedeEditar" color="red" @click="deleteItem">{{$t('common.eliminar')}}</f7-button>
        </template>
      </f7-nav-right>
    </f7-navbar>

    <r-form
      ref="rform"
      v-slot="{rform}"
      :itemId="id"
      @save="onSave"
      @loadItem="onLoadItem"
      :readonly="readonly"
      :dbAdapter="Gasto"
      :documentoAdapter="Documento"
      :defaultData="defaultData"
      :beforeSave="beforeSave"
    >
      <f7-row no-gap>
        <!-- Columna del justificante: la foto y lo que dice la IA -->
        <f7-col width="100" medium="40">
          <f7-card class="padding-vertical-half padding-right">
            <f7-card-header>{{$t('gastos.justificante')}}</f7-card-header>
            <f7-card-content>
              <!-- ignorarOffline: la foto va al servidor con el gasto también desde la tablet (el
                   registro de gastos es online), así que rFiles la trata como en la web (base64). -->
              <r-files
                ref="rfiles"
                :form="rform"
                name="documentos"
                :documentoAdapter="Documento"
                :maxFiles="1"
                :path="'gastos/' + (rform.formData.username || usuario) + '/'"
                :ignorarOffline="true"
                :mostrarComentario="false"
                :label="$t('gastos.adjuntar_foto')"
                :labelFoto="$t('gastos.hacer_foto')"
                :conservarNombre="false"
                :sobrescribir="false"
                :readonly="readonly"
                :itemHeight="220"
                @loadFile="onFotoCargada"
              ></r-files>
              <div class="gasto-ia-acciones" v-if="!readonly">
                <f7-button
                  fill
                  small
                  icon-f7="sparkles"
                  :text="leyendo ? $t('gastos.leyendo') : $t('gastos.leer_ticket')"
                  :disabled="leyendo || !hayFoto(rform)"
                  @click="leerTicket"
                ></f7-button>
                <f7-preloader v-if="leyendo" size="22"></f7-preloader>
              </div>

              <!-- Resultado del reconocimiento -->
              <div v-if="rform.formData.reconocimiento && rform.formData.reconocimiento.estado" class="gasto-reconocimiento" :class="'estado-' + rform.formData.reconocimiento.estado">
                <div class="gasto-reconocimiento-titulo">
                  <f7-icon :f7="iconoReconocimiento(rform.formData.reconocimiento)"></f7-icon>
                  <strong>{{ $t('gastos.reconocimiento.' + rform.formData.reconocimiento.estado) }}</strong>
                </div>
                <p v-if="rform.formData.reconocimiento.mensaje">{{ rform.formData.reconocimiento.mensaje }}</p>
                <p v-if="rform.formData.reconocimiento.aviso" class="gasto-aviso">{{ rform.formData.reconocimiento.aviso }}</p>
                <p v-if="rform.formData.reconocimiento.modelo" class="gasto-detalle">
                  {{ rform.formData.reconocimiento.modelo }}
                  <span v-if="rform.formData.reconocimiento.confianza != null"> · {{$t('gastos.confianza')}} {{ Math.round(rform.formData.reconocimiento.confianza * 100) }}%</span>
                </p>
                <ul v-if="conceptos(rform).length" class="gasto-conceptos">
                  <li v-for="(c, i) in conceptos(rform)" :key="i">
                    <span>{{ c.descripcion }}</span>
                    <span v-if="c.importe != null">{{ formatearImporte(c.importe, rform.formData.moneda) }}</span>
                  </li>
                </ul>
              </div>

              <!-- Histórico de correcciones -->
              <div v-if="rform.formData.historicoCambios && rform.formData.historicoCambios.length" class="gasto-historico">
                <div class="gasto-reconocimiento-titulo">
                  <f7-icon f7="pencil_circle"></f7-icon>
                  <strong>{{$t('gastos.historico')}}</strong>
                </div>
                <ul>
                  <li v-for="(c, i) in rform.formData.historicoCambios" :key="i">
                    <span class="gasto-historico-campo">{{ $t('gastos.campos.' + c.campo) }}</span>:
                    <span class="gasto-historico-antes">{{ mostrarValor(c.valorAnterior) }}</span>
                    <f7-icon f7="arrow_right" size="12"></f7-icon>
                    <span class="gasto-historico-despues">{{ mostrarValor(c.valorNuevo) }}</span>
                    <span class="gasto-detalle"> · {{ c.usuario }} · {{ formatearFechaHora(c.fecha) }}</span>
                  </li>
                </ul>
              </div>
            </f7-card-content>
          </f7-card>
        </f7-col>

        <!-- Columna de datos -->
        <f7-col width="100" medium="60">
          <f7-card class="padding-vertical-half padding-right">
            <f7-card-content>
              <f7-row no-gap class="list list-form no-hairlines">
                <f7-col width="100" medium="50">
                  <r-autocomplete
                    v-if="veTodos && !id"
                    outline
                    :dbAdapter="Empleado"
                    class="col-100"
                    floating-label
                    :label="$t('gastos.campos.empleado')"
                    :form="rform"
                    name="username"
                    labelProp="nombre"
                    idProp="username"
                    prefixProp="username"
                    :shownFields="['username', 'nombre']"
                    :async="true"
                    :minLength="2"
                  ></r-autocomplete>
                  <r-input
                    v-else
                    outline
                    class="col-100"
                    floating-label
                    :label="$t('gastos.campos.empleado')"
                    :form="rform"
                    type="text"
                    :name="rform.formData.nombreEmpleado ? 'nombreEmpleado' : 'username'"
                    :readonly="true"
                    :overrideReadonly="true"
                  ></r-input>
                </f7-col>
                <!-- Los <option> llegan del servidor después de montarse el select: el <select> nativo
                     se queda con el valor que tenía cuando aún no había opciones y no lo repinta al
                     llegar (se veía el valor solo en el tooltip). Con la clave, el select se vuelve a
                     montar cuando llegan las opciones y toma el valor del formulario. -->
                <f7-col width="50" medium="25">
                  <r-input :key="'tipo-' + catalogos.tiposDocumento.length" outline class="col-100" floating-label :label="$t('gastos.campos.tipoDocumento')" :form="rform" type="select" name="tipoDocumento">
                    <option v-for="t in catalogos.tiposDocumento" :key="t.codigo" :value="t.codigo">{{ t.nombre }}</option>
                  </r-input>
                </f7-col>
                <f7-col width="50" medium="25">
                  <r-input :key="'categoria-' + catalogos.categorias.length" outline class="col-100" floating-label :label="$t('gastos.campos.categoria')" :form="rform" type="select" name="categoria">
                    <option v-for="c in catalogos.categorias" :key="c.codigo" :value="c.codigo">{{ c.nombre }}</option>
                  </r-input>
                </f7-col>

                <f7-col width="50" medium="25">
                  <r-input
                    outline
                    class="col-100"
                    floating-label
                    :label="$t('gastos.campos.fecha')"
                    :form="rform"
                    name="fecha"
                    type="date"
                    :calendar-params="{ closeOnSelect: true, routableModals: false, dateFormat: { year: 'numeric', month: '2-digit', day: '2-digit' } }"
                    :formatDate="'DD/MM/YYYY'"
                  ></r-input>
                </f7-col>
                <f7-col width="50" medium="25">
                  <r-input outline class="col-100" floating-label :label="$t('gastos.campos.hora')" :form="rform" type="text" name="hora" placeholder="HH:mm"></r-input>
                </f7-col>
                <f7-col width="100" medium="50">
                  <r-input outline class="col-100" floating-label :label="$t('gastos.campos.establecimiento')" :form="rform" type="text" name="establecimiento"></r-input>
                </f7-col>
                <f7-col width="100">
                  <!-- Dirección, teléfono, web... del negocio, una por línea, como vienen en el ticket. -->
                  <r-input outline class="col-100 gasto-datos-establecimiento" floating-label :label="$t('gastos.campos.datosEstablecimiento')" :form="rform" type="textarea" name="datosEstablecimiento" :resizable="true"></r-input>
                </f7-col>

                <f7-col width="50" medium="25">
                  <r-input outline class="col-100" floating-label :label="$t('gastos.campos.cif')" :form="rform" type="text" name="cif"></r-input>
                </f7-col>
                <f7-col width="50" medium="25">
                  <r-input outline class="col-100" floating-label :label="$t('gastos.campos.numeroFactura')" :form="rform" type="text" name="numeroFactura"></r-input>
                </f7-col>
                <f7-col width="50" medium="25">
                  <r-input outline class="col-100" floating-label :label="$t('gastos.campos.matricula')" :form="rform" type="text" name="matricula" :allMayus="true"></r-input>
                </f7-col>
                <f7-col width="50" medium="25">
                  <r-autocomplete
                    outline
                    :dbAdapter="Inspeccion"
                    class="col-100"
                    floating-label
                    :label="$t('gastos.campos.codigoExpediente')"
                    :form="rform"
                    name="codigoExpediente"
                    labelProp="codigo"
                    idProp="codigo"
                    :shownFields="['id', 'codigo', 'cliente.razonSocial']"
                    :async="true"
                    :minLength="3"
                    :onchange="onExpedienteElegido"
                  ></r-autocomplete>
                </f7-col>

                <f7-col width="50" medium="25">
                  <r-input outline class="col-100" floating-label :label="$t('gastos.campos.baseImponible')" :form="rform" type="number" step="0.01" name="baseImponible"></r-input>
                </f7-col>
                <f7-col width="50" medium="25">
                  <r-input outline class="col-100" floating-label :label="$t('gastos.campos.iva')" :form="rform" type="number" step="0.01" name="iva"></r-input>
                </f7-col>
                <f7-col width="50" medium="25">
                  <r-input outline class="col-100" floating-label :label="$t('gastos.campos.tipoIva')" :form="rform" type="number" step="1" name="tipoIva"></r-input>
                </f7-col>
                <f7-col width="50" medium="25">
                  <r-input outline class="col-100 gasto-total" floating-label :label="$t('gastos.campos.importeTotal')" :form="rform" type="number" step="0.01" name="importeTotal"></r-input>
                </f7-col>

                <f7-col width="100">
                  <r-input outline class="col-100" floating-label :label="$t('gastos.campos.observaciones')" :form="rform" type="textarea" name="observaciones"></r-input>
                </f7-col>
              </f7-row>
              <div v-if="rform.formData.modificado" class="gasto-corregido">
                <f7-icon f7="pencil_circle_fill" size="16"></f7-icon> {{$t('gastos.corregido')}}
              </div>
            </f7-card-content>
          </f7-card>
        </f7-col>
      </f7-row>
    </r-form>
  </f7-page>
</template>

<style scoped>
.gasto-ia-acciones {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 8px 0 4px 16px;
}
.gasto-reconocimiento,
.gasto-historico {
  margin: 12px 0 4px 16px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f6f8fb;
  font-size: 13px;
}
.gasto-reconocimiento.estado-RECONOCIDO { border-color: #b7e4c7; background: #f1faf4; }
.gasto-reconocimiento.estado-ERROR { border-color: #f5c2c7; background: #fdf3f4; }
.gasto-reconocimiento.estado-SIN_RECONOCER { border-color: #d4dde8; background: #f6f8fb; }
.gasto-reconocimiento-titulo {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}
.gasto-reconocimiento p { margin: 4px 0; }
.gasto-aviso { color: #9a6700; }
.gasto-detalle { color: #6c757d; font-size: 12px; }
.gasto-conceptos {
  margin: 6px 0 0;
  padding-left: 0;
  list-style: none;
}
.gasto-conceptos li {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 2px 0;
  border-bottom: 1px dashed #e2e8f0;
}
.gasto-historico ul { margin: 0; padding-left: 0; list-style: none; }
.gasto-historico li { padding: 3px 0; }
.gasto-historico-campo { font-weight: 600; }
.gasto-historico-antes { color: #b02a37; text-decoration: line-through; }
.gasto-historico-despues { color: #146c43; font-weight: 600; }
.gasto-corregido {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 4px 16px;
  font-size: 12px;
  color: #9a6700;
}
::v-deep .gasto-total input { font-weight: 700; }
::v-deep .gasto-datos-establecimiento textarea { min-height: 72px; white-space: pre-wrap; }
</style>

<script>
import rForm from "./../../components/rForm.vue";
import rInput from "./../../components/rInput.vue";
import rFiles from "./../../components/rFiles.vue";
import rAutocomplete from "./../../components/rAutocomplete.vue";
import Gasto from "./../../js/db/Gasto.js";
import Documento from "./../../js/db/Documento.js";
import Empleado from "./../../js/db/Empleado.js";
import Inspeccion from "./../../js/db/Inspeccion.js";
import Utils from "./../../js/Utils.js";
import moment from "moment";

/** Campos que propone la IA; se rellenan desde `reconocimiento.datos` con el mismo nombre. */
const CAMPOS_IA = ["tipoDocumento", "fecha", "hora", "establecimiento", "datosEstablecimiento", "cif",
  "numeroFactura", "matricula", "baseImponible", "iva", "tipoIva", "importeTotal", "moneda", "categoria"];

export default {
  components: {
    rForm,
    rInput,
    rFiles,
    rAutocomplete
  },
  props: {
    id: {
      type: String
    },
    editable: {
      type: Boolean,
      default: true
    }
  },
  data: function() {
    const user = Utils.getUser() || {};
    return {
      Gasto,
      Documento,
      Empleado,
      Inspeccion,
      readonly: !this.editable,
      leyendo: false,
      lecturaEnCurso: 0,
      relanzarLectura: false,
      usuario: user.username || "",
      veTodos: false,
      catalogos: { categorias: [], tiposDocumento: [] },
      itemCargado: null,
      defaultData: {
        // El gasto es del usuario de la sesión salvo que administración elija otro empleado.
        username: user.username || "",
        nombreEmpleado: user.nombre || "",
        tipoDocumento: "TICKET",
        categoria: "OTROS",
        moneda: "EUR",
        documentos: [],
        reconocimiento: null,
        origen: process.env.TARGET === "cordova" ? "CORDOVA" : "WEB",
        // Para que un reintento por corte de red no duplique el gasto en el servidor.
        idLocal: "local-" + Date.now() + "-" + Math.floor(Math.random() * 1e6)
      }
    };
  },
  computed: {
    puedeEditar: function() {
      if (!this.itemCargado) return false;
      return this.veTodos || (this.usuario && this.itemCargado.username === this.usuario);
    }
  },
  mounted: function() {
    const self = this;
    // Foto elegida desde el botón «Nuevo gasto» del listado: se adjunta y se lee en cuanto el
    // formulario tiene sus datos por defecto.
    const pendiente = !self.id ? Gasto.tomarFotoPendiente() : null;
    if (pendiente) {
      self.$nextTick(function() {
        const rform = self.$refs.rform;
        if (!rform || !rform.formData) return;
        self.$set(rform.formData, "documentos", [pendiente]);
        self.onFotoCargada();
      });
    }
    Gasto.catalogos()
      .then(function(c) {
        self.catalogos = {
          categorias: c.categorias || [],
          tiposDocumento: c.tiposDocumento || []
        };
        self.veTodos = !!c.veTodos;
      })
      .catch(function(e) {
        console.error("No se han podido cargar los catálogos de gastos", e);
      });
  },
  methods: {
    onLoadItem: function(item) {
      // Copia profunda: rForm monta formData con un spread superficial, así que `documentos` sería
      // el mismo array y borrar la foto en el formulario la borraría también de aquí (y Cancelar
      // no la devolvería).
      this.itemCargado = item ? JSON.parse(JSON.stringify(item)) : item;
    },
    hayFoto: function(rform) {
      const docs = rform && rform.formData && rform.formData.documentos;
      return Array.isArray(docs) && docs.length > 0;
    },
    conceptos: function(rform) {
      const rec = rform && rform.formData && rform.formData.reconocimiento;
      const datos = rec && rec.datos;
      return datos && Array.isArray(datos.conceptos) ? datos.conceptos : [];
    },
    iconoReconocimiento: function(rec) {
      if (rec.estado === "RECONOCIDO") return "checkmark_seal_fill";
      if (rec.estado === "ERROR") return "exclamationmark_triangle_fill";
      return "questionmark_circle";
    },
    /** Un 0 es un valor (IVA exento); solo el vacío se pinta como raya. */
    mostrarValor: function(v) {
      return v === null || v === undefined || v === "" ? "—" : v;
    },
    /** Importe con la moneda del gasto (la que leyó la IA o la que eligió el empleado), no siempre euros. */
    formatearImporte: function(v, moneda) {
      const codigo = /^[A-Z]{3}$/.test(String(moneda || "").toUpperCase()) ? String(moneda).toUpperCase() : "EUR";
      try {
        return Number(v).toLocaleString("es-ES", { style: "currency", currency: codigo });
      } catch (e) {
        return Number(v).toLocaleString("es-ES", { minimumFractionDigits: 2 }) + " " + codigo;
      }
    },
    formatearFechaHora: function(f) {
      return f ? moment(f).format("DD/MM/YYYY HH:mm") : "";
    },
    onExpedienteElegido: function(componente) {
      const self = this;
      return function(value) {
        const item = value && value[0];
        componente.setValue(item);
        const rform = self.$refs.rform;
        if (rform && rform.formData) {
          self.$set(rform.formData, "idExpediente", item && item.id ? item.id : null);
        }
      };
    },
    /**
     * Al adjuntar la foto se lanza la lectura sola; el botón queda para repetirla. Antes se vacía lo
     * que la IA leyó de la foto anterior: si la lectura nueva falla, no puede quedar un ticket con
     * los datos de otro. Lo del empleado (expediente, observaciones) se conserva.
     */
    onFotoCargada: function() {
      if (this.readonly) return;
      this.limpiarLectura();
      this.leerTicket();
    },
    limpiarLectura: function() {
      const formData = this.$refs.rform && this.$refs.rform.formData;
      if (!formData) return;
      this.$set(formData, "reconocimiento", null);
      CAMPOS_IA.forEach((campo) => {
        if (campo === "tipoDocumento") this.$set(formData, campo, "TICKET");
        else if (campo === "categoria") this.$set(formData, campo, "OTROS");
        else if (campo === "moneda") this.$set(formData, campo, "EUR");
        else this.$set(formData, campo, null);
      });
    },
    /** Base64 de la foto: la recién adjuntada la trae rFiles; una ya guardada se baja del servidor. */
    base64DeLaFoto: function(doc) {
      if (doc._base64) {
        // rFiles guarda el data URL del FileReader ("data:image/jpeg;base64,...."): se separa el
        // mime del base64 a pelo, que es lo que espera /gasto/extraer.
        const partes = /^data:([^;,]+);base64,(.*)$/s.exec(doc._base64);
        return Promise.resolve(partes
          ? { base64: partes[2], mimeType: partes[1] }
          : { base64: doc._base64, mimeType: null });
      }
      return Documento.getDocumento(doc, "arraybuffer").then(function(buffer) {
        const bytes = new Uint8Array(buffer);
        let binario = "";
        for (let i = 0; i < bytes.length; i += 0x8000) {
          binario += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
        }
        return { base64: window.btoa(binario), mimeType: null };
      });
    },
    /** Si `doc` sigue siendo la foto del formulario (misma referencia o mismo contenido). */
    esFotoActual: function(doc) {
      const rform = this.$refs.rform;
      const actual = rform && rform.formData && rform.formData.documentos && rform.formData.documentos[0];
      if (!actual || !doc) return false;
      if (actual === doc) return true;
      return !!(doc._base64 && actual._base64 === doc._base64) || !!(doc.path && actual.path === doc.path);
    },
    leerTicket: function() {
      const self = this;
      const app = self.$f7;
      const rform = self.$refs.rform;
      if (!rform || !self.hayFoto(rform)) return;
      if (self.leyendo) {
        // Ya hay una lectura en vuelo de otra foto: cuando termine se relanzará para la actual.
        self.relanzarLectura = true;
        return;
      }
      const doc = rform.formData.documentos[0];
      // Número de esta lectura: cancelar la edición lo cambia, y una respuesta que llegue después
      // se descarta aunque la foto restaurada sea la misma.
      const lectura = ++self.lecturaEnCurso;
      self.leyendo = true;
      self.relanzarLectura = false;
      self.base64DeLaFoto(doc)
        .then(function(foto) {
          return Gasto.extraer({
            nombreFichero: doc.extension ? doc.nombre + "." + doc.extension : doc.nombre,
            mimeType: foto.mimeType,
            base64: foto.base64
          });
        })
        .then(function(res) {
          if (lectura !== self.lecturaEnCurso || self.readonly) {
            // Se canceló la edición (o empezó otra lectura) mientras la IA leía: no se toca el formulario.
            return;
          }
          if (!self.esFotoActual(doc)) {
            // La foto cambió mientras la IA leía: lo leído es de un ticket que ya no está.
            self.relanzarLectura = true;
            return;
          }
          const tickets = (res && res.tickets) || [];
          if (!tickets.length) {
            // La IA no ha reconocido un ticket ni una factura (o no ha respondido). Queda
            // registrado en el gasto tal cual; el empleado rellena los datos a mano.
            const error = res && res.error;
            self.$set(rform.formData, "reconocimiento", error || null);
            app.dialog.alert(
              (error && error.mensaje) || self.$t("gastos.error_lectura_generico"),
              self.$t("gastos.reconocimiento.ERROR")
            );
            return;
          }
          if (tickets.length > 1 && !self.id) {
            // Varios tickets en la misma foto: a la pantalla de lote, una línea por ticket con su
            // recorte ya subido, y un solo «Guardar todos». Esta alta se abandona (no hay gasto).
            Gasto.ponerLote(tickets, doc);
            self.$f7router.navigate("/gastos/lote/", { reloadCurrent: true });
            return;
          }
          if (tickets.length > 1) {
            // Editando un gasto que ya existe: un gasto es UN justificante, así que se queda con el
            // primer ticket (y su recorte) y se avisa; irse al lote dejaría este gasto sin tocar y
            // crearía otros nuevos sin que el empleado lo esperase.
            app.dialog.alert(
              self.$t("gastos.varios_tickets_en_edicion", {
                total: tickets.length,
                establecimiento: (tickets[0].reconocimiento && tickets[0].reconocimiento.datos && tickets[0].reconocimiento.datos.establecimiento) || "—"
              }),
              self.$t("gastos.lote_titulo", { total: tickets.length })
            );
          }
          self.aplicarTicket(tickets[0]);
          const rec = tickets[0].reconocimiento;
          app.toast.create({
            text: self.$t("gastos.leido_ok") + (rec && rec.aviso ? " " + rec.aviso : ""),
            position: "center",
            closeTimeout: rec && rec.aviso ? 6000 : 2500,
            icon: '<i class="f7-icons">sparkles</i>'
          }).open();
        })
        .catch(function(e) {
          if (lectura !== self.lecturaEnCurso || self.readonly) return;
          if (!self.esFotoActual(doc)) {
            self.relanzarLectura = true;
            return;
          }
          app.dialog.alert(e && (e.message || e.error) ? (e.message || e.error) : self.$t("gastos.error_lectura_generico"));
        })
        .finally(function() {
          if (lectura !== self.lecturaEnCurso) return; // otra lectura (o la cancelación) ya manda
          self.leyendo = false;
          if (self.relanzarLectura && !self.readonly) {
            self.relanzarLectura = false;
            self.leerTicket();
          }
        });
    },
    /** Pone en el formulario el ticket detectado: su lectura firmada (con un solo ticket vale la foto entera). */
    aplicarTicket: function(ticket) {
      const formData = this.$refs.rform && this.$refs.rform.formData;
      if (!formData || !ticket) return;
      if (ticket.documento && ticket.documento.path) {
        this.$set(formData, "documentos", [ticket.documento]);
      }
      this.$set(formData, "reconocimiento", ticket.reconocimiento || null);
      if (ticket.reconocimiento && ticket.reconocimiento.estado === "RECONOCIDO") {
        this.rellenarDesde(ticket.reconocimiento.datos || {});
      }
    },
    /** Vuelca lo propuesto por la IA en el formulario. Se pisan los campos: el empleado ha pedido leer el ticket. */
    rellenarDesde: function(datos) {
      const self = this;
      const formData = self.$refs.rform.formData;
      CAMPOS_IA.forEach(function(campo) {
        let valor = datos[campo];
        if (valor === undefined || valor === null || valor === "") return;
        if (campo === "fecha") {
          // yyyy-MM-dd a medianoche LOCAL: si se dejara en UTC, en España sería el día anterior a las 23:00.
          const partes = String(valor).split("-");
          valor = partes.length === 3 ? new Date(Number(partes[0]), Number(partes[1]) - 1, Number(partes[2])) : new Date(valor);
        } else if (campo === "tipoDocumento" || campo === "categoria" || campo === "moneda") {
          // Los <option> comparan el valor exacto: en mayúsculas, como los enums del servidor.
          valor = String(valor).trim().toUpperCase();
        }
        self.$set(formData, campo, valor);
      });
    },
    beforeSave: function(data, resolve, reject) {
      const self = this;
      // rForm enseña `message` al rechazar; `error` se deja por si lo lee otro consumidor.
      const rechazar = function(clave) {
        const texto = self.$t(clave);
        reject({ error: texto, message: texto });
      };
      // El aspa del autocompletado vacía codigoExpediente sin pasar por onchange: el id oculto
      // tiene que irse con el código, o el gasto seguiría enlazado a un expediente que no se ve.
      if (!data.codigoExpediente) {
        data.idExpediente = null;
      }
      if (!data.documentos || !data.documentos.length) {
        rechazar("gastos.foto_obligatoria");
        return;
      }
      // La foto pendiente de subir (lleva `_base64`) se eligió antes de abrir el formulario, con la
      // carpeta del usuario logado: si administración ha puesto el gasto a nombre de otro, se
      // recoloca en la carpeta del dueño. rForm sube DESPUÉS de este beforeSave, con este `path`.
      const usuarioSesion = Utils.getUser() || {};
      const carpeta = "gastos/" + (data.username || usuarioSesion.username || "") + "/";
      data.documentos.forEach(function(d) {
        if (d && d._base64) d.path = carpeta;
      });
      if (!data.importeTotal || Number(data.importeTotal) <= 0) {
        rechazar("gastos.total_obligatorio");
        return;
      }
      if (!data.fecha) {
        rechazar("gastos.fecha_obligatoria");
        return;
      }
      resolve(data);
    },
    guardar: function() {
      // El botón ya va deshabilitado mientras lee; esto cubre el atajo de teclado y los dobles clics.
      if (this.leyendo) return;
      this.$refs.rform.saveItem();
    },
    cancelarEdicion: function() {
      const self = this;
      // Una lectura en vuelo ya no cuenta: si llegara después pisaría el formulario restaurado.
      self.lecturaEnCurso += 1;
      self.leyendo = false;
      self.relanzarLectura = false;
      if (self.itemCargado) {
        self.$refs.rform.formData = JSON.parse(JSON.stringify(self.itemCargado));
      }
      self.readonly = true;
    },
    onSave: function(res) {
      const self = this;
      const app = self.$f7;
      const eraNuevo = !self.id;
      app.toast.create({
        text: self.$t("common.guardado_ok"),
        position: "center",
        closeTimeout: 2000,
        icon: '<i class="f7-icons">checkmark_alt</i>'
      }).open();
      if (eraNuevo && res && res.id) {
        // En el alta se va a la ficha en modo lectura, con lo que ha devuelto el servidor
        // (histórico y marca de corregido incluidos).
        self.$f7router.navigate("/gastos/view/" + res.id + "/", { reloadCurrent: true });
        return;
      }
      self.$refs.rform.formData = res;
      self.itemCargado = JSON.parse(JSON.stringify(res));
      self.readonly = true;
    },
    deleteItem: function() {
      const self = this;
      const app = self.$f7;
      app.dialog.confirm(self.$t("rlist.pregunta_eliminar_registro"), self.$t("common.eliminar"), function() {
        // Con la versión con la que se abrió la ficha: si alguien lo editó entre medias, la API
        // contesta conflicto en vez de borrar lo que ese otro acaba de cambiar.
        const version = self.$refs.rform && self.$refs.rform.formData ? self.$refs.rform.formData.version : null;
        Gasto.delete({ itemId: self.id, version: version })
          .then(function(res) {
            if (res) {
              app.toast.create({
                icon: '<i class="f7-icons">checkmark</i>',
                text: self.$t("rlist.registro_eliminado"),
                position: "center",
                destroyOnClose: true,
                closeTimeout: 2000
              }).open();
              self.$f7router.back();
            }
          })
          .catch(function(error) {
            app.dialog.alert(error.message || error.error || error);
          });
      });
    }
  }
};
</script>
